if (!customElements.get('bertic-rotating-products')) {
  customElements.define(
    'bertic-rotating-products',
    class BerticRotatingProducts extends HTMLElement {
      static STORAGE_KEY = 'berticdesigns_rotating_products';
      // Taille de page du paginate de sections/bertic-rotating-products-pool.liquid
      static POOL_PAGE_SIZE = 100;
      // Limite de pages parcourues pour réunir les séries de la réserve (un ajout se fait souvent par séries entières)
      static POOL_MAX_PAGES = 10;
      // Pages de la réserve récupérées en parallèle, pour raccourcir l'attente au premier affichage de la session
      static POOL_PAGES_PER_BATCH = 3;
      // Cartes déjà récupérées, gardées pour la session (sessionStorage) : affichage immédiat aux visites suivantes
      static CARDS_KEY = 'berticdesigns_rotating_products_cards';
      static CARDS_MAX = 60;
      // Ajoutée par le script en ligne de la section dès l'affichage, retirée quand la rangée est prête
      static LOADING_CLASS = 'bertic-rotating-products__row--loading';

      connectedCallback() {
        this.rows = [...this.querySelectorAll('.bertic-rotating-products__row')];
        if (this.rows.length) this.rotate().finally(() => this.rows.forEach((row) => this.revealRow(row)));
      }

      revealRow(row) {
        row.classList.remove(BerticRotatingProducts.LOADING_CLASS);
      }

      async rotate() {
        // Un tirage enregistré n'est réutilisé que si les réglages de la section et la langue n'ont pas changé
        const signature = [
          this.dataset.locale,
          this.dataset.maxPerSeries,
          ...this.rows.map((row) =>
            [row.dataset.collectionUrl, row.dataset.sort, row.dataset.poolSeries, this.getItems(row).length].join(' ')
          ),
        ].join('|');

        let selection = this.readSelection(signature);
        if (!selection) {
          selection = await this.drawSelection();
          // Réserves indisponibles (erreur réseau) : rien n'est conservé, pour retenter à la prochaine visite
          if (selection.some((handles) => handles.length)) this.writeSelection(signature, selection);
        }

        const displayed = await Promise.all(this.rows.map((row, index) => this.displayRow(row, selection[index])));
        // Produit supprimé ou dépublié depuis le tirage : nouveau tirage à la prochaine visite
        if (displayed.includes(false)) this.writeSelection(signature, null);
      }

      getItems(row) {
        return [...row.querySelectorAll(':scope > ul > li')];
      }

      // Stockage selon la rotation : par session (sessionStorage), par jour (localStorage, avec la date) ou à chaque visite (aucun)
      getStorage() {
        try {
          if (this.dataset.rotation === 'session') return sessionStorage;
          if (this.dataset.rotation === 'day') return localStorage;
        } catch (error) {
          // Stockage indisponible (navigation privée) : nouveau tirage à chaque visite
        }
        return null;
      }

      today() {
        const date = new Date();
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
      }

      readAll(storage) {
        try {
          const stored = JSON.parse(storage.getItem(BerticRotatingProducts.STORAGE_KEY) ?? '{}');
          return stored && typeof stored === 'object' && !Array.isArray(stored) ? stored : {};
        } catch (error) {
          return {};
        }
      }

      readSelection(signature) {
        const storage = this.getStorage();
        if (!storage) return null;

        const stored = this.readAll(storage)[this.dataset.sectionId];
        if (stored?.signature !== signature || !Array.isArray(stored.rows)) return null;
        if (this.dataset.rotation === 'day' && stored.date !== this.today()) return null;
        return stored.rows;
      }

      writeSelection(signature, rows) {
        const storage = this.getStorage();
        if (!storage) return;

        const all = this.readAll(storage);
        if (rows) {
          all[this.dataset.sectionId] = { signature, date: this.today(), rows };
        } else {
          delete all[this.dataset.sectionId];
        }
        try {
          storage.setItem(BerticRotatingProducts.STORAGE_KEY, JSON.stringify(all));
        } catch (error) {
          // Quota dépassé : le tirage n'est simplement pas conservé
        }
      }

      // Une liste de handles par rangée ; une liste vide garde la sélection du serveur (rangée fixe ou réserve indisponible)
      async drawSelection() {
        const pools = await Promise.all(this.rows.map((row) => this.fetchPool(row)));

        // Les rangées qui gardent la sélection du serveur réservent leurs produits, pour éviter les doublons
        const shown = new Set();
        pools.forEach((pool, index) => {
          if (!pool) this.getItems(this.rows[index]).forEach((item) => shown.add(item.dataset.handle));
        });

        return this.rows.map((row, index) => {
          if (!pools[index]) return [];

          const picked = this.draw(pools[index], this.getItems(row).length, shown, row.dataset.sort !== 'random');
          if (picked.length < this.getItems(row).length) {
            this.getItems(row).forEach((item) => shown.add(item.dataset.handle));
            return [];
          }
          picked.forEach((handle) => shown.add(handle));
          return picked;
        });
      }

      // Tirage uniforme par série (une série de 30 produits n'a pas plus de chances qu'une série de 2),
      // au plus max-per-series produits par série, puis sans limite si les séries ne suffisent pas
      draw(pool, count, shown, keepPoolOrder) {
        const maxPerSeries = parseInt(this.dataset.maxPerSeries, 10) || 1;
        const groups = new Map();
        pool
          .filter((product) => !shown.has(product.handle))
          .forEach((product) => {
            if (!groups.has(product.series)) groups.set(product.series, []);
            groups.get(product.series).push(product.handle);
          });

        const series = this.shuffle([...groups.values()].map((handles) => this.shuffle(handles)));
        const picked = [];
        for (let round = 0; round < maxPerSeries; round++) {
          series.forEach((handles) => {
            if (picked.length < count && handles[round]) picked.push(handles[round]);
          });
        }
        if (picked.length < count) {
          const rest = this.shuffle(series.flat().filter((handle) => !picked.includes(handle)));
          picked.push(...rest.slice(0, count - picked.length));
        }

        // Nouveautés et meilleures ventes restent dans l'ordre de la réserve (plus récent, plus vendu en premier)
        if (keepPoolOrder) {
          const positions = new Map(pool.map((product, index) => [product.handle, index]));
          picked.sort((a, b) => positions.get(a) - positions.get(b));
        }
        return picked;
      }

      shuffle(array) {
        for (let i = array.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [array[i], array[j]] = [array[j], array[i]];
        }
        return array;
      }

      // Aucune réserve pour une rangée fixe (ordre de la collection) ; une page au hasard pour le fond de catalogue ;
      // sinon les produits des pool-series premières séries selon le tri, en parcourant la collection page par page
      async fetchPool(row) {
        if (row.dataset.sort === 'collection') return null;

        if (row.dataset.sort === 'random') {
          const pages = Math.ceil(parseInt(row.dataset.productsCount, 10) / BerticRotatingProducts.POOL_PAGE_SIZE) || 1;
          return this.fetchPoolPage(row, { page: 1 + Math.floor(Math.random() * pages) });
        }

        const { POOL_MAX_PAGES, POOL_PAGES_PER_BATCH, POOL_PAGE_SIZE } = BerticRotatingProducts;
        const wantedSeries = parseInt(row.dataset.poolSeries, 10);
        const series = new Set();
        const pool = [];
        for (let firstPage = 1; firstPage <= POOL_MAX_PAGES; firstPage += POOL_PAGES_PER_BATCH) {
          const pageNumbers = [];
          for (let page = firstPage; page < firstPage + POOL_PAGES_PER_BATCH && page <= POOL_MAX_PAGES; page++) {
            pageNumbers.push(page);
          }
          const pages = await Promise.all(
            pageNumbers.map((page) => this.fetchPoolPage(row, { sort_by: row.dataset.sort, page }))
          );

          for (const [index, products] of pages.entries()) {
            // Erreur sur une page suivante : la réserve se contente des séries déjà réunies
            if (!products) return firstPage === 1 && index === 0 ? null : pool;

            for (const product of products) {
              if (!series.has(product.series)) {
                if (series.size >= wantedSeries) return pool;
                series.add(product.series);
              }
              pool.push(product);
            }
            if (products.length < POOL_PAGE_SIZE) return pool;
          }
        }
        return pool;
      }

      async fetchPoolPage(row, params) {
        const query = new URLSearchParams({ section_id: 'bertic-rotating-products-pool', ...params });
        try {
          const response = await fetch(`${row.dataset.collectionUrl}?${query}`);
          if (!response.ok) return null;

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const products = JSON.parse(html.querySelector('[data-bertic-pool]')?.textContent ?? 'null')?.products;
          return Array.isArray(products) ? products : null;
        } catch (error) {
          return null;
        }
      }

      // Renvoie false si une carte n'a pas pu être récupérée : la rangée garde alors sa sélection par défaut
      async displayRow(row, handles) {
        const items = this.getItems(row);
        if (!Array.isArray(handles) || handles.length !== items.length) return true;
        if (handles.every((handle, index) => handle === items[index].dataset.handle)) return true;

        const rootUrl = this.dataset.rootUrl.replace(/\/$/, '');
        const cards = await Promise.all(handles.map((handle) => this.fetchCard(rootUrl, handle)));
        if (cards.some((card) => !card)) return false;

        // Délai de sécurité dépassé : la sélection par défaut est déjà visible et n'est plus remplacée
        if (!row.classList.contains(BerticRotatingProducts.LOADING_CLASS)) return true;

        items.forEach((item, index) => {
          item.dataset.handle = handles[index];
          item.replaceChildren(cards[index]);
        });
        this.revealRow(row);
        return true;
      }

      readCards() {
        try {
          const stored = JSON.parse(sessionStorage.getItem(BerticRotatingProducts.CARDS_KEY) ?? 'null');
          return stored?.locale === this.dataset.locale && Array.isArray(stored.cards) ? stored.cards : [];
        } catch (error) {
          return [];
        }
      }

      // Entrées [handle, html], de la plus récente à la plus ancienne
      writeCard(handle, html) {
        const cards = this.readCards().filter(([storedHandle]) => storedHandle !== handle);
        cards.unshift([handle, html]);
        try {
          sessionStorage.setItem(
            BerticRotatingProducts.CARDS_KEY,
            JSON.stringify({ locale: this.dataset.locale, cards: cards.slice(0, BerticRotatingProducts.CARDS_MAX) })
          );
        } catch (error) {
          // Stockage indisponible ou plein : la carte sera simplement récupérée de nouveau
        }
      }

      parseCard(html) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector('.card-wrapper');
      }

      async fetchCard(rootUrl, handle) {
        const cached = this.readCards().find(([storedHandle]) => storedHandle === handle);
        if (cached) return this.parseCard(cached[1]);

        try {
          const response = await fetch(`${rootUrl}/products/${encodeURIComponent(handle)}?section_id=bertic-product-card`);
          if (!response.ok) return null;

          const card = this.parseCard(await response.text());
          if (card) this.writeCard(handle, card.outerHTML);
          return card;
        } catch (error) {
          return null;
        }
      }
    }
  );
}
